// Google Ads dashboard view-state persistence — PURE.
//
// The tree LEVEL selection, the visible metric columns, the KPI tiles and every
// chart choice survive reloads via the
// generic `User.gridPreferences` mechanism (gridKey 'google-ads'), the same store
// the accounts grid and the Meta reporting explorer use. The store's shape is
// `{ activePresetId, presets: [{ id, name, visibleColumns: string[] }] }`, so the
// state is encoded as tokens in one reserved preset's `visibleColumns`:
// `lvl:<level>` · `col:<metricKey>` · `attr:<attributeKey>` · `kpi:<metricKey>` ·
// `chart:<chartKey>` · `table:<tableKey>` ·
// `big:<metricKey>` · `trend:<metricKey>` · `group:<grouping>` ·
// `style:<chartStyle>` · `tstyle:<metricKey>:<chartStyle>` · `charts:none` ·
// `tables:none` · `cbcol:<metricKey>` (Performance-by-campaign-type columns) ·
// `stcol:<fieldKey>` (Search-terms columns) ·
// `ccy:<code>` (the agency display currency; absent = native) ·
// `wgt:<id>:<vizType>:<metricKey>:<flags>` (one user-added widget) ·
// `can:<kind>:<id>:<x>:<y>:<w>:<h>` (ONE panel's place on the canvas, lg-only).
//
// ⚠️ `table:` (which tables are shown) and `can:table:` (where one sits) are
// DIFFERENT tokens and are decoded by different prefixes — neither can match the
// other, since a canvas token always starts `can:`. A test pins that.
//
// ⚠️ THE `lay:tile:` / `lay:chart:` / `lay:table:` / `lay:widget:` TOKENS OF THE
// PREVIOUS BUILD ARE GONE. The top section used to be four separate grids, each
// with its own coordinate space; it is now ONE canvas, so those coordinates mean
// nothing and a stored arrangement resets once. See chartLayout.ts.
//
// Reuses the existing PATCH /api/me/preferences endpoint with NO server change —
// exactly the device explorerPrefs.ts uses on the Meta side. Legacy/absent prefs
// carry no tokens, so both fall back to the defaults.

import type { GridPreferences } from '../../types/user';
import type { GoogleAdsLevel, GoogleAdsMetricKey } from '../../types/googleAdsReporting';
import {
  DEFAULT_GOOGLE_ADS_LEVELS,
  canonicalizeLevels,
} from '../../lib/googleAdsLevels';
import {
  GOOGLE_ADS_DEFAULT_COLUMNS,
  GOOGLE_ADS_DEFAULT_KPIS,
  GOOGLE_ADS_DEFAULT_TREND,
  GOOGLE_ADS_METRIC_ORDER,
  isGoogleAdsMetricKey,
  sanitizeMetricKeys,
} from '../../lib/googleAdsMetrics';
import {
  DEFAULT_GOOGLE_CHARTS,
  GOOGLE_CHARTS,
  sanitizeCharts,
  type GoogleChartKey,
} from './googleAdsChartCatalog';
import {
  canvasPanelKey,
  decodeCanvasLayout,
  encodeCanvasLayout,
  type ChartPanelLayout,
} from '../../lib/reporting/chartLayout';
import {
  decodeWidgets,
  encodeWidgets,
  type ReportingWidget,
} from '../../lib/reporting/widgets';
import { isGoogleWidgetDimension } from './googleWidgetDimensions';
import { sanitizeAttributeKeys, type GoogleAdsAttributeKey } from './attributeColumns';
import {
  CHANNEL_BREAKDOWN_DEFAULT_COLUMNS,
  DEFAULT_GOOGLE_TABLES,
  GOOGLE_TABLES,
  SEARCH_TERM_DEFAULT_COLUMNS,
  sanitizeChannelColumns,
  sanitizeSearchTermColumns,
  sanitizeTables,
  type GoogleTableKey,
  type SearchTermColumnKey,
} from './tableWidgets';
import { DEFAULT_TREND_GROUPING, type TrendGrouping } from '../reporting/chartDate';
import type { ChartStyle } from '../reporting/chartPrefs';
import { decodeCurrencyToken } from '../reporting/chartPrefs';
import {
  NATIVE_DISPLAY_CURRENCY,
  isNativeDisplay,
} from '../../lib/reporting/currencyConversion';

/**
 * Every canvas panel key this dashboard can produce — the union of its KPI
 * tiles, its charts, its two secondary tables and the user's current widgets,
 * each KIND-QUALIFIED.
 *
 * ⚠️ THE UNION IS THE PRUNING CATALOGUE, and it must include panels that are
 * currently HIDDEN (an unselected metric, a chart or table toggled off) — that
 * is what lets a panel come back to where the user left it rather than being
 * dumped at the bottom. Only keys OUTSIDE it are pruned, so a retired panel or a
 * deleted widget can't leave a `can:` token accumulating forever.
 *
 * ⚠️ Kind-qualified because the four populations share ONE coordinate space now:
 * `trend` is both a chart key and an ordinary metric-shaped string, and a widget
 * id is arbitrary.
 */
export function googleAdsCanvasPanelKeys(widgets: readonly ReportingWidget[]): string[] {
  return [
    ...GOOGLE_ADS_METRIC_ORDER.map((k) => canvasPanelKey('tile', k)),
    ...GOOGLE_CHARTS.map((c) => canvasPanelKey('chart', c.key)),
    ...GOOGLE_TABLES.map((t) => canvasPanelKey('table', t.key)),
    ...widgets.map((w) => canvasPanelKey('widget', w.id)),
  ];
}

export const GOOGLE_ADS_GRID_KEY = 'google-ads';

/** The reserved preset id the state hides under (never 'default' — that's built-in). */
const STATE_PRESET_ID = 'state';

export interface GoogleAdsViewState {
  levels: GoogleAdsLevel[]; // canonical order, never empty
  columns: GoogleAdsMetricKey[]; // catalogue order, never empty
  /**
   * The ATTRIBUTE columns (campaign type, budget, ad strength…). Catalogue
   * order, and MAY be empty — empty is the default, not a cleared selection:
   * most of them are not wanted most of the time, so the table opens as Name +
   * metrics and an AM adds what they need. Parallel to `columns` so the coming
   * save model can treat both uniformly.
   */
  attributes: GoogleAdsAttributeKey[];
  /** KPI tiles (each carries a mini-chart). Catalogue order, never empty. */
  kpis: GoogleAdsMetricKey[];
  /** Which page charts are shown. MAY be empty — hiding them all is a choice. */
  charts: GoogleChartKey[];
  /**
   * Which of the two secondary TABLES are shown (Performance by campaign type ·
   * Search terms). MAY be empty, same posture as `charts`.
   */
  tables: GoogleTableKey[];
  /**
   * Per-table column selections. Both fall back to their default set when empty
   * — a table showing only its row labels reads as broken, not as a choice.
   *
   * ⚠️ Two different vocabularies on purpose: `channelColumns` is the shared
   * metric catalogue MINUS the impression-share family (see tableWidgets.ts for
   * why that exclusion must survive), `searchTermColumns` is the search-terms
   * row's own scalar fields and nothing else.
   */
  channelColumns: GoogleAdsMetricKey[];
  searchTermColumns: SearchTermColumnKey[];
  /** The metric the big chart plots (a KPI tile click sets it). */
  bigChartMetric: GoogleAdsMetricKey;
  /** The series ticked onto the MULTI-KPI trend chart. */
  trendMetrics: GoogleAdsMetricKey[];
  /**
   * The agency DISPLAY CURRENCY — 'native' (per-currency, the default) or a code
   * every money figure is converted into. Same posture as the Meta side: stored
   * as-typed here and resolved against the live rate table at USE time, so a
   * currency temporarily missing from the table degrades to Native rather than
   * having the preference erased.
   */
  displayCurrency: string;
  /** Bucket grouping + the big chart's style. */
  grouping: TrendGrouping;
  chartStyle: ChartStyle;
  /**
   * PER-TILE mini-chart style, keyed by metric — the Meta hero-card model, where
   * each tile carries its own bar/line/area toggle. A metric absent from the map
   * uses the default, so only non-default choices are persisted.
   */
  tileStyles: Record<string, ChartStyle>;
  /**
   * The user-added widgets, in render order. EMPTY is the fresh-user state AND
   * what a legacy pref decodes to — the canvas simply carries no widget panels,
   * which is right for both. Unlike `columns` / `kpis`, empty never falls back
   * to a default set: a widget the user did not ask for is not a sensible
   * default.
   */
  widgets: ReportingWidget[];
  /**
   * The ONE canvas arrangement — tiles, charts, tables and widgets together,
   * each panel kind-qualified. EMPTY means "use the default arrangement", which
   * is what a legacy pref decodes to AND what "Reset layout" saves, so there is
   * a single path back to the default.
   */
  canvasLayout: ChartPanelLayout[];
}

export const DEFAULT_GOOGLE_ADS_VIEW_STATE: GoogleAdsViewState = {
  levels: [...DEFAULT_GOOGLE_ADS_LEVELS],
  columns: [...GOOGLE_ADS_DEFAULT_COLUMNS],
  attributes: [],
  kpis: [...GOOGLE_ADS_DEFAULT_KPIS],
  charts: [...DEFAULT_GOOGLE_CHARTS],
  tables: [...DEFAULT_GOOGLE_TABLES],
  channelColumns: [...CHANNEL_BREAKDOWN_DEFAULT_COLUMNS],
  searchTermColumns: [...SEARCH_TERM_DEFAULT_COLUMNS],
  bigChartMetric: 'cost',
  trendMetrics: [...GOOGLE_ADS_DEFAULT_TREND],
  displayCurrency: NATIVE_DISPLAY_CURRENCY,
  grouping: DEFAULT_TREND_GROUPING,
  chartStyle: 'bar',
  tileStyles: {},
  widgets: [],
  canvasLayout: [],
};

export const DEFAULT_TILE_STYLE: ChartStyle = 'bar';

/** A tile's chart style — the default unless the user set one. */
export function tileStyleFor(
  styles: Record<string, ChartStyle>,
  key: GoogleAdsMetricKey,
): ChartStyle {
  const s = styles[key];
  return s && VALID_STYLES.includes(s) ? s : DEFAULT_TILE_STYLE;
}

const VALID_GROUPINGS: TrendGrouping[] = ['daily', 'weekly', 'monthly'];
const VALID_STYLES: ChartStyle[] = ['bar', 'line', 'area'];

/** Encode the state into a GridPreferences envelope the preferences endpoint accepts. */
export function encodeGoogleAdsViewState(state: GoogleAdsViewState): GridPreferences {
  const levels = canonicalizeLevels(state.levels);
  const columns = sanitizeMetricKeys(state.columns, GOOGLE_ADS_DEFAULT_COLUMNS);
  return {
    activePresetId: STATE_PRESET_ID,
    presets: [
      {
        id: STATE_PRESET_ID,
        name: 'Google Ads view state',
        visibleColumns: [
          ...levels.map((l) => `lvl:${l}`),
          ...columns.map((c) => `col:${c}`),
          // ⚠️ No "none" marker is needed here, unlike `charts:none`. An empty
          // attribute selection and a legacy pref that predates attributes mean
          // the SAME thing — no attribute columns — because empty IS the
          // default. A marker would only be needed if absence had to decode to
          // something other than empty.
          ...sanitizeAttributeKeys(state.attributes).map((a) => `attr:${a}`),
          ...sanitizeMetricKeys(state.kpis, GOOGLE_ADS_DEFAULT_KPIS).map((k) => `kpi:${k}`),
          ...sanitizeCharts(state.charts).map((c) => `chart:${c}`),
          ...sanitizeTables(state.tables).map((t) => `table:${t}`),
          // Per-table columns, each in its OWN namespace and each sanitized by
          // its own vocabulary — which is what stops an impression-share key
          // reaching the campaign-type table even if one were stored by hand.
          ...sanitizeChannelColumns(state.channelColumns).map((c) => `cbcol:${c}`),
          ...sanitizeSearchTermColumns(state.searchTermColumns).map((c) => `stcol:${c}`),
          ...sanitizeMetricKeys(state.trendMetrics, GOOGLE_ADS_DEFAULT_TREND).map(
            (k) => `trend:${k}`,
          ),
          `big:${state.bigChartMetric}`,
          `group:${state.grouping}`,
          // Native is the DEFAULT, so absence means Native and only a real
          // currency is written — no "none" marker needed (unlike charts/tables,
          // where absence had to decode to something OTHER than empty).
          ...(isNativeDisplay(state.displayCurrency) ? [] : [`ccy:${state.displayCurrency}`]),
          `style:${state.chartStyle}`,
          // Only NON-default tile styles are stored — the map stays small and a
          // future change of default applies to everyone who never chose one.
          ...Object.entries(state.tileStyles)
            .filter(([, v]) => VALID_STYLES.includes(v) && v !== DEFAULT_TILE_STYLE)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => `tstyle:${k}:${v}`),
          // ⚠️ An EMPTY chart selection must survive the round trip, and a list of
          // tokens cannot express "none" by absence — absence is what a legacy
          // pref looks like, and that has to decode to the DEFAULTS. This marker
          // distinguishes "the user hid every chart" from "this pref predates
          // charts entirely".
          ...(sanitizeCharts(state.charts).length === 0 ? ['charts:none'] : []),
          // Same marker, same reason: "both tables hidden" and "this pref
          // predates the tables region" are indistinguishable by absence, and
          // the second must decode to the DEFAULTS.
          ...(sanitizeTables(state.tables).length === 0 ? ['tables:none'] : []),
          // The user-added widgets. No "none" marker is needed, unlike
          // `charts:none` / `tables:none`: an empty widget list and a legacy pref
          // that predates widgets mean the SAME thing — no widgets — because
          // empty IS the default here.
          ...encodeWidgets(state.widgets),
          // The canvas, pruned to the union catalogue on the way out so a
          // retired chart, a dropped metric or a removed widget can't leave a
          // token living forever. A HIDDEN panel's token is deliberately KEPT —
          // re-enabling it restores where the user put it rather than dumping it
          // at the bottom.
          ...encodeCanvasLayout(
            state.canvasLayout.filter((i) =>
              googleAdsCanvasPanelKeys(state.widgets).includes(i.key),
            ),
          ),
        ],
      },
    ],
  };
}

/**
 * Decode a stored envelope back into state. Never throws, and never yields an
 * empty selection: a cleared or malformed stored value falls back to the defaults,
 * because an empty level list would render a table with no rows and no
 * explanation — which reads as broken rather than as a choice.
 */
export function decodeGoogleAdsViewState(
  prefs: GridPreferences | undefined,
): GoogleAdsViewState {
  const preset = prefs?.presets.find((p) => p.id === STATE_PRESET_ID);
  if (!preset) return cloneDefault();
  const tokens = preset.visibleColumns;

  const levelTokens = tokens.filter((t) => t.startsWith('lvl:')).map((t) => t.slice(4));
  const colTokens = tokens.filter((t) => t.startsWith('col:')).map((t) => t.slice(4));
  const attrTokens = tokens.filter((t) => t.startsWith('attr:')).map((t) => t.slice(5));

  const chartTokens = tokens.filter((t) => t.startsWith('chart:')).map((t) => t.slice(6));
  // ⚠️ `table:` never matches a `lay:table:` layout token — a layout token always
  // starts `lay:`. The two namespaces are independent by construction.
  const tableTokens = tokens.filter((t) => t.startsWith('table:')).map((t) => t.slice(6));
  const cbColTokens = tokens.filter((t) => t.startsWith('cbcol:')).map((t) => t.slice(6));
  const stColTokens = tokens.filter((t) => t.startsWith('stcol:')).map((t) => t.slice(6));
  const kpiTokens = tokens.filter((t) => t.startsWith('kpi:')).map((t) => t.slice(4));
  const trendTokens = tokens.filter((t) => t.startsWith('trend:')).map((t) => t.slice(6));
  const big = tokens.find((t) => t.startsWith('big:'))?.slice(4);

  const tileStyles: Record<string, ChartStyle> = {};
  for (const t of tokens) {
    if (!t.startsWith('tstyle:')) continue;
    const rest = t.slice('tstyle:'.length);
    // The metric key can't contain ':', but split from the RIGHT anyway so a
    // future key with one still resolves its style rather than silently dropping.
    const sep = rest.lastIndexOf(':');
    if (sep <= 0) continue;
    const key = rest.slice(0, sep);
    const style = rest.slice(sep + 1) as ChartStyle;
    if (VALID_STYLES.includes(style)) tileStyles[key] = style;
  }
  const group = tokens.find((t) => t.startsWith('group:'))?.slice(6);
  const style = tokens.find((t) => t.startsWith('style:'))?.slice(6);
  // Shared with the Meta decoder — one definition of what a stored currency
  // token means, so the two dashboards can't disagree about a stale code.
  const displayCurrency = decodeCurrencyToken(
    tokens.find((t) => t.startsWith('ccy:'))?.slice('ccy:'.length),
  );
  const hidAllCharts = tokens.includes('charts:none');
  const hidAllTables = tokens.includes('tables:none');

  return {
    // canonicalizeLevels already falls back on an empty/garbage list.
    levels: canonicalizeLevels(levelTokens),
    columns: sanitizeMetricKeys(colTokens, GOOGLE_ADS_DEFAULT_COLUMNS),
    attributes: sanitizeAttributeKeys(attrTokens),
    displayCurrency,
    kpis: sanitizeMetricKeys(kpiTokens, GOOGLE_ADS_DEFAULT_KPIS),
    charts: hidAllCharts
      ? []
      : chartTokens.length > 0
        ? sanitizeCharts(chartTokens)
        : [...DEFAULT_GOOGLE_CHARTS],
    tables: hidAllTables
      ? []
      : tableTokens.length > 0
        ? sanitizeTables(tableTokens)
        : [...DEFAULT_GOOGLE_TABLES],
    // Each sanitizer falls back to its own default set when nothing usable was
    // stored, so a legacy pref opens the tables exactly as they are today.
    channelColumns: sanitizeChannelColumns(cbColTokens),
    searchTermColumns: sanitizeSearchTermColumns(stColTokens),
    bigChartMetric: sanitizeMetricKeys(big ? [big] : [], ['cost'])[0]!,
    trendMetrics: sanitizeMetricKeys(trendTokens, GOOGLE_ADS_DEFAULT_TREND),
    tileStyles,
    // ⚠️ Scoped to THIS dashboard's catalogue — a widget token carrying a Meta
    // metric key (or one retired from GOOGLE_ADS_METRICS) is dropped rather than
    // defaulted onto some other metric.
    widgets: decodeWidgets(tokens, isGoogleAdsMetricKey, isGoogleWidgetDimension),
    canvasLayout: decodeCanvasLayout(tokens),
    grouping: VALID_GROUPINGS.includes(group as TrendGrouping)
      ? (group as TrendGrouping)
      : DEFAULT_TREND_GROUPING,
    chartStyle: VALID_STYLES.includes(style as ChartStyle) ? (style as ChartStyle) : 'bar',
  };
}

function cloneDefault(): GoogleAdsViewState {
  return {
    ...DEFAULT_GOOGLE_ADS_VIEW_STATE,
    levels: [...DEFAULT_GOOGLE_ADS_VIEW_STATE.levels],
    columns: [...DEFAULT_GOOGLE_ADS_VIEW_STATE.columns],
    attributes: [],
    kpis: [...DEFAULT_GOOGLE_ADS_VIEW_STATE.kpis],
    charts: [...DEFAULT_GOOGLE_ADS_VIEW_STATE.charts],
    tables: [...DEFAULT_GOOGLE_ADS_VIEW_STATE.tables],
    channelColumns: [...DEFAULT_GOOGLE_ADS_VIEW_STATE.channelColumns],
    searchTermColumns: [...DEFAULT_GOOGLE_ADS_VIEW_STATE.searchTermColumns],
    trendMetrics: [...DEFAULT_GOOGLE_ADS_VIEW_STATE.trendMetrics],
    tileStyles: {},
    widgets: [],
    canvasLayout: [],
  };
}
